/**
 * DIE DATEI AUS DEM CRM (Auftrag CRM-168).
 *
 * Jede Maske, die eine Datei liefert — Beleg, Sammelabrechnung, Aushang,
 * Datenträger, Berichtsexport, Teilnehmerliste —, reicht sie über diese eine
 * Stelle. Vorher stand derselbe Fünfzeiler in sechs Masken.
 *
 * Ein PDF wird nach dem Erzeugen gleich im Betrachter des Browsers geöffnet,
 * in einem neuen Reiter. Am Rechner wird es außerdem heruntergeladen wie
 * bisher. Auf Tablet und Telefon wird es NUR geöffnet: Safari fragte sonst
 * zweimal, und gesichert wird dort über das Teilen-Menü des Betrachters.
 * CSV, XLSX und alles andere bleiben reiner Download.
 *
 * DER REITER GEHT IM KLICK AUF, NICHT NACH DEM LADEN.
 *
 * Safari (und mit ihm jedes iPad) lässt `window.open` nur zu, solange der
 * Klick noch läuft. Nach einem `await` ist er vorbei, und das Öffnen wird
 * still verworfen. `ladeUndReiche` öffnet den Reiter deshalb, BEVOR es auf
 * den Server wartet, zeigt darin „PDF wird erstellt …" und lenkt ihn danach
 * auf die Datei. Geht das Laden schief, schließt es den Reiter wieder; die
 * Meldung zeigt die Maske wie bisher. Darum muss `ladeUndReiche` im
 * Klickpfad vor dem ersten `await` aufgerufen werden.
 *
 * KEIN `noopener` IM AUFRUF — ABER DIE VERBINDUNG WIRD GEKAPPT.
 *
 * Mit `noopener` liefert `window.open` immer `null`. Dann ließe sich der im
 * Klick geöffnete Reiter nicht mehr auf die Datei lenken, und eine Sperre
 * wäre von einem Erfolg nicht zu unterscheiden. Stattdessen setzt die Hilfe
 * gleich nach dem Öffnen `reiter.opener = null`: Der neue Reiter kommt nicht
 * mehr an das CRM-Fenster heran, nur das CRM-Fenster an ihn. Geladen wird
 * darin ohnehin nur ein PDF, das das CRM selbst erzeugt hat.
 */

/** Was ein Ladeweg liefert. */
export type Datei = { readonly blob: Blob; readonly dateiname: string };

/** Der Hinweis, wenn der Browser das Öffnen verhindert hat. */
export const OEFFNEN_GESPERRT = 'Die Datei ist heruntergeladen; das Öffnen hat der Browser verhindert.';

/**
 * So lange bleibt der Objektverweis eines geöffneten PDF stehen. Der Reiter
 * lädt die Datei erst, nachdem er hingelenkt wurde; gibt man den Verweis im
 * selben Zug frei, zeigt er eine leere Seite. Danach wird er frei — sonst
 * hielte der Browser jedes PDF eines Nachmittags bis zum Neuladen im Speicher.
 */
export const FREIGABE_NACH_MS = 60_000;

const WARTETEXT = 'PDF wird erstellt …';

/** Ob die Datei ein PDF ist — nach Typ oder, wo der Server keinen nennt, nach Endung. */
export function istPdf(blob: Blob, dateiname: string): boolean {
  return blob.type === 'application/pdf' || istPdfName(dateiname);
}

export function istPdfName(dateiname: string): boolean {
  return /\.pdf$/i.test(dateiname);
}

/**
 * Ob das Gerät ein Tablet oder Telefon ist: grober Zeiger, oder iOS/iPadOS.
 * Ein iPad meldet sich als Mac, verrät sich aber durch den Touchschirm.
 */
export function nurOeffnen(): boolean {
  if (typeof window === 'undefined') return false;
  const grob = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;
  const kennung = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(kennung) || (/Macintosh/.test(kennung) && navigator.maxTouchPoints > 1);
  return grob || ios;
}

/**
 * Öffnet einen leeren Reiter mit Wartetext — synchron im Klick aufzurufen.
 * `null` heißt: Der Browser hat das Öffnen gesperrt.
 */
export function oeffneReiter(): Window | null {
  const reiter = oeffne('');
  if (reiter === null) return null;
  try {
    reiter.document.title = WARTETEXT;
    reiter.document.body.textContent = WARTETEXT;
  } catch {
    // Nur der Wartetext fehlt; die Datei kommt trotzdem.
  }
  return reiter;
}

/**
 * Holt eine Datei und reicht sie (siehe oben). Ist ein PDF zu erwarten, geht
 * der Reiter sofort auf — noch im Klick. Gibt den Hinweis zurück, den die
 * Maske zeigen soll, sonst `null`. Ein Fehler beim Laden wird weitergeworfen.
 */
export async function ladeUndReiche(laden: () => Promise<Datei>, erwartePdf: boolean): Promise<string | null> {
  const reiter = erwartePdf ? oeffneReiter() : undefined;
  let datei: Datei;
  try {
    datei = await laden();
  } catch (ursache) {
    schliesse(reiter);
    throw ursache;
  }
  return reicheDatei(datei.blob, datei.dateiname, reiter);
}

/**
 * Reicht eine schon vorliegende Datei.
 *
 * `reiter`: der vorab geöffnete Reiter, `null` wenn er gesperrt war, oder
 * nicht angegeben — dann versucht die Hilfe selbst zu öffnen, was nur im
 * Klick gelingt.
 */
export function reicheDatei(blob: Blob, dateiname: string, reiter?: Window | null): string | null {
  if (!istPdf(blob, dateiname)) {
    schliesse(reiter);
    lade(blob, dateiname);
    return null;
  }

  const verweis = URL.createObjectURL(blob);
  let ziel: Window | null;
  if (reiter === undefined) {
    ziel = oeffne(verweis);
  } else {
    ziel = reiter !== null && !reiter.closed ? reiter : null;
    if (ziel !== null) lenke(ziel, verweis);
  }

  if (ziel === null) {
    // Gesperrt — oder den Wartereiter schon wieder zugemacht. Dann wenigstens die Datei.
    klicke(verweis, dateiname);
    URL.revokeObjectURL(verweis);
    return reiter === undefined || reiter === null ? OEFFNEN_GESPERRT : null;
  }

  if (!nurOeffnen()) klicke(verweis, dateiname);
  window.setTimeout(() => URL.revokeObjectURL(verweis), FREIGABE_NACH_MS);
  return null;
}

/** Reiner Download; der Objektverweis wird gleich wieder frei. */
function lade(blob: Blob, dateiname: string): void {
  const verweis = URL.createObjectURL(blob);
  klicke(verweis, dateiname);
  URL.revokeObjectURL(verweis);
}

function klicke(verweis: string, dateiname: string): void {
  const anker = document.createElement('a');
  anker.href = verweis;
  anker.download = dateiname;
  anker.click();
}

function oeffne(adresse: string): Window | null {
  let reiter: Window | null | undefined;
  try {
    reiter = window.open(adresse, '_blank');
  } catch {
    return null;
  }
  // jsdom und manche Sperren liefern `undefined` statt `null`.
  if (reiter === null || reiter === undefined) return null;
  try {
    reiter.opener = null;
  } catch {
    // Der Reiter liegt schon auf einem fremden Ursprung — dann reicht er ohnehin nicht zurück.
  }
  return reiter;
}

function lenke(reiter: Window, verweis: string): void {
  reiter.location.href = verweis;
}

function schliesse(reiter: Window | null | undefined): void {
  if (reiter !== null && reiter !== undefined && !reiter.closed) reiter.close();
}
